import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import DownloadExcelFile from "helpers/excelDownloadHandler";
import { usePermission } from "hooks/usePermission";
import { Download } from "lucide-react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadTransactionsReports,
  downloadTransactionsReportsExcel,
} from "store/slices/reportsDownloadSlice";
import {
  getTransactionsList,
  resetFilters,
  setFilter,
} from "store/slices/reportsSlice";
import { ORDER_BY_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { CsvIcon, ExcelIcon, formatUTCDate, getMinMaxDate } from "utils/utils";
import RefundInitiationForm from "./RefundInitiationForm";

const TransactionsList = () => {
  const { t } = useTranslation();
  const { noPlans } = Icons || {};
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canView } = canAction("report");
  const exportMenuRef = useRef(null);
  const [refundInitiationModal, setRefundInitiationModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundedTransactionIds, setRefundedTransactionIds] = useState([]);

  const {
    loading,
    totalPages,
    transactions,
    currentPage,
    totalCount,
    filters: { keyword, dates, orderBy },
  } = useSelector((state) => state.Reports);
  const { submitRefundForm: isRefundFormSubmitting } = useSelector(
    (state) => state.Customers,
  );

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getTransactionsList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Invoice Number"),
        key: "invoiceNumber",
      },
      {
        header: t("Customer"),
        key: "userId",
        render: (value) => (
          <div className="ltr_text">
            <p>{value?.name}</p>
            <p>{value?.phone}</p>
          </div>
        ),
      },
      {
        header: t("Membership Number"),
        key: "userMembershipId",
        render: (value) => (
          <div className="ltr_text">
            <p>{value?.membershipNo}</p>
          </div>
        ),
      },
      {
        header: t("Plan Type"),
        key: "userMembershipId",
        render: (value) => (
          <div className="ltr_text">
            <p>{value?.planType}</p>
          </div>
        ),
      },
      {
        header: t("License Plate"),
        key: "userMembershipId",
        render: (value) => (
          <div className="ltr_text">
            <p>{value?.licencePlateNumber}</p>
          </div>
        ),
      },
      {
        header: t("Amount"),
        key: "amount",
      },
      {
        header: t("Payment Method"),
        key: "paymentMethod",
      },
      {
        header: t("Gateway Status"),
        key: "gatewayStatus",
        accessor: (value) => (
          <div className="ltr_text">
            <p>
              {refundedTransactionIds.includes(value?._id)
                ? t("REFUNDED")
                : value?.gatewayStatus}
            </p>
            {value?.gatewayStatus === "CAPTURED" &&
              value?.amount > 0 &&
              !refundedTransactionIds.includes(value?._id) && (
                <button
                  onClick={() => handleOpenRefundModal(value)}
                  title={t("Initiate refund")}
                  className="refund_btn"
                >
                  {t("Initiate Refund")}
                </button>
              )}
          </div>
        ),
      },
      {
        header: t("Transaction Id"),
        key: "transactionId",
      },
      {
        header: t("Platform"),
        key: "platform",
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
    ],
    [t, refundedTransactionIds],
  );
  const addRefundedTransactionId = (id) => {
    setRefundedTransactionIds((prev) => [...prev, id]);
  };
  const handleDownloadTransactionsCSV = (action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      orderBy: isReset ? -1 : orderBy,
      exportType: "csv",
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(downloadTransactionsReports(payload)).then((result) => {
      const data = result?.payload ?? result;
      DownloadExcelFile(data, "transactions");
    });
  };
  const handleDownloadTransactionsExcel = (action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      orderBy: isReset ? -1 : orderBy,
      exportType: "excel",
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(downloadTransactionsReportsExcel(payload)).then((result) => {
      const data = result?.payload ?? result;
      DownloadExcelFile(data, "transactions");
    });
  };
  const handleOpenRefundModal = (transaction) => {
    setSelectedTransaction(transaction);
    setRefundInitiationModal(true);
  };

  const handleRefundSubmit = () => {
    handleRefundInitiationModalClose();
  };
  const exportMenuItems = [
    {
      label: t("Export as CSV"),
      icon: <CsvIcon />,
      command: handleDownloadTransactionsCSV,
    },
    {
      label: t("Export as Excel"),
      icon: <ExcelIcon />,
      command: handleDownloadTransactionsExcel,
    },
  ];
  const extraActions = (
    <div className="buttons export_buttons">
      <Menu
        model={exportMenuItems}
        popup
        ref={exportMenuRef}
        id="export-menu"
      />
      <Button
        label={t("Export")}
        icon={<Download size={16} />}
        onClick={(e) => exportMenuRef.current.toggle(e)}
        aria-controls="export-menu"
        aria-haspopup
        className="p-button-outlined"
      />
    </div>
  );

  const handleRefundInitiationModalClose = () => {
    setRefundInitiationModal(false);
    setSelectedTransaction(null);
  };

  if (!canView) return null;

  return (
    <div className="page-content transactions_page">
      <h4 className="page_title mb-3">{t("Transactions")}</h4>
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        filters={filtersList}
        selectedFilters={{ keyword, orderBy }}
        keywordPlaceholder={t("Search by Invoice Number")}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={false}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        rightActions={extraActions}
      />

      {loading && <TableShimmer rows={12} columns={6} />}

      {!loading && transactions?.length === 0 && (
        <NoDataFound title={t("No vehicle color found")} icon={noPlans} />
      )}
      {!loading && transactions?.length > 0 && (
        <>
          <DataTable columns={columns} data={transactions} t={t} />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
      <Modal
        show={refundInitiationModal}
        onHide={handleRefundInitiationModalClose}
        centered
        className="common_modal"
      >
        <Modal.Header closeButton className="pt-0">
          <Modal.Title>{t("Initiate Refund")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <RefundInitiationForm
            transactionId={selectedTransaction?._id}
            amount={selectedTransaction?.amount}
            onSubmit={handleRefundSubmit}
            isLoading={isRefundFormSubmitting}
            addRefundedTransactionId={addRefundedTransactionId}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TransactionsList;
